import styled from 'styled-components';

import { GENERAL_SECTIONS } from 'components/notificationPreferences/generalSections';
import InboundReferralSectionPreferences from 'components/notificationPreferences/InboundReferralSectionPreferences';
import { PreferencesFormMeta, PreferencesFormValues } from 'components/notificationPreferences/NotificationPreferences';
import Toggle from 'components/shared/Toggle.tsx';
import { useForm } from 'context/form';
import { GeneralPreference } from 'models/Notifications';
import { BodySmall, H3 } from 'styles/typography';

// The one form key the General row lives under. Its siblings are the care-coordination rows,
// keyed by group-type ids (uuids), and the inbound-referrals key, so it never collides.
export const GENERAL_FORM_KEY = 'general';

function GeneralPreferencesContainer({ disabled = false }: { disabled?: boolean }) {
  const { values, setValue } = useForm<PreferencesFormValues, PreferencesFormMeta>();

  const preference = (values[GENERAL_FORM_KEY] ?? {}) as GeneralPreference;

  return (
    <Container>
      <Banner $active={!!preference.active}>
        <BannerText>
          <BannerTitle>General Notifications</BannerTitle>
          <BannerSubtitle>
            {preference.active ? 'All notifications are on for General' : 'Notifications are disabled for General'}
          </BannerSubtitle>
        </BannerText>
        <Toggle
          active={!!preference.active}
          disabled={disabled}
          onChange={(active) => setValue(`${GENERAL_FORM_KEY}.active`, active)}
        />
      </Banner>
      <SelectSection>
        <StyledH3>Select Notifications</StyledH3>
        <SectionDescription>Choose which events trigger a notification</SectionDescription>
        {GENERAL_SECTIONS.map((section) => (
          <InboundReferralSectionPreferences
            key={section.category}
            section={section}
            formKey={GENERAL_FORM_KEY}
            testIdPrefix='general-section'
            disabled={disabled || !preference.active}
          />
        ))}
      </SelectSection>
    </Container>
  );
}

const Container = styled.div`
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Banner = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.blue100 : theme.colors.black10)};
  background-color: ${({ $active, theme }) => ($active ? theme.colors.blue5 : theme.colors.white)};
  padding: 16px 24px;
  transition: 0.2s;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BannerTitle = styled.div`
  color: ${({ theme }) => theme.colors.black100};
  font-size: 14px;
  font-weight: 500;
`;

const BannerSubtitle = styled(BodySmall)`
  font-size: 12px;
`;

const SelectSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledH3 = styled(H3)`
  margin-bottom: 12px;
`;

const SectionDescription = styled(BodySmall)`
  font-size: 12px;
  margin-bottom: 24px;
`;

export default GeneralPreferencesContainer;
